import { useLayoutEffect } from 'react';

const variables = ['left', 'top', 'width', 'height'];

function visibleRect(element) {
  if (!element || element.closest('[aria-hidden="true"]') || !element.getClientRects().length
    || getComputedStyle(element).visibility === 'hidden') return null;
  const rect = element.getBoundingClientRect();
  return rect.width && rect.height && rect.top >= 0 && rect.top < window.innerHeight ? rect : null;
}

// The title lives outside <header> in the feed. Read the current card only;
// incoming/outgoing cards can both exist while a swipe is animating.
export function updateMessageLayout() {
  const root = document.getElementById('root');
  if (!root || root.querySelector('.news-container[aria-busy="true"]')) return;
  const current = root.querySelector('.current-news');
  const title = visibleRect((current || root).querySelector('.storyTitle'));
  const control = visibleRect(root.querySelector('header.header .post-filter .ant-btn-icon'))
    || visibleRect(root.querySelector('header.header .pageName'));
  const values = title ? {
    left: title.left,
    width: title.width,
    top: Math.min(control?.top ?? title.top, title.top),
    height: title.bottom - Math.min(control?.top ?? title.top, title.top),
  } : null;
  for (const name of variables) {
    const property = `--wiw-message-${name}`;
    if (values) document.documentElement.style.setProperty(property, `${values[name]}px`);
    else document.documentElement.style.removeProperty(property);
  }
}

export default function MessageLayout() {
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    let frame;
    let disposed = false;
    const resize = new ResizeObserver(() => schedule());
    let observed = [];
    const update = () => {
      frame = null;
      if (disposed) return;
      const next = [...root.querySelectorAll('header.header, .current-news .storyTitle, .author-info .storyTitle, header .storyTitle')];
      if (next.length !== observed.length || next.some((element, index) => element !== observed[index])) {
        resize.disconnect();
        next.forEach(element => resize.observe(element));
        observed = next;
      }
      // Notices and user-action menus measure on opening. While none are mounted, feed
      // scrolling/mutations need not read bounds or rewrite geometry variables.
      if (document.querySelector('.wiw-message-body, .user-actions-menu, .user-block-confirm')) updateMessageLayout();
    };
    const schedule = () => { if (!frame && !disposed) frame = requestAnimationFrame(update); };
    const mutation = new MutationObserver(schedule);
    mutation.observe(root, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'aria-busy'],
    });
    update();
    window.addEventListener('resize', schedule);
    document.addEventListener('scroll', schedule, true);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    document.fonts?.ready.then(schedule);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      mutation.disconnect();
      resize.disconnect();
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      variables.forEach(name => document.documentElement.style.removeProperty(`--wiw-message-${name}`));
    };
  }, []);
  return null;
}
